import { type Money, ZERO, add, sub, sum, mul, clampAtZero } from "../money/money";
import type { CartLineInput } from "./price-cart";
import { allocatePromoDiscount } from "./promo-discount";

/**
 * Module-granted line discounts: "grants".
 *
 * An extension may give ONE unit of a cart line away. Core does not know or
 * care why — a reward, a birthday, a comp — only that the unit's price comes off
 * and that the extension that asked is named on the discount row
 * (`discount_application.granted_by_module`).
 *
 * WHAT A GRANT COVERS. The variant's unit price, once. Never its modifiers: an
 * extra shot or oat milk on a free drink is still charged, because the grant is
 * for the item, not for whatever was added to it. Never more than the line
 * costs, so a line can never go below zero.
 *
 * ORDER, fixed (specs/006-loyalty/research.md R3):
 *
 *   line extension → grant → promo over (subtotal − grant) → line rounding
 *     → subtotal → statutory over line totals after grants → tax → total
 *
 * So a percentage off the order never takes a percentage off something already
 * free, and a statutory discount sees a free drink as only its paid add-ons.
 *
 * Pure: no I/O, no clock, no randomness. The caller supplies new line ids.
 */

export class GrantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GrantError";
  }
}

/** One unit's price, without modifiers, capped at what the line costs. */
export function grantAmount(line: CartLineInput): Money {
  const modifierTotal = sum(line.modifiers.map((m) => m.priceAdjustment));
  const perUnit = clampAtZero(add(line.unitPrice, modifierTotal));
  const lineSubtotal = mul(perUnit, line.quantity);
  const unit = clampAtZero(line.unitPrice);
  return unit > lineSubtotal ? lineSubtotal : unit;
}

/**
 * Make sure a single unit can be granted.
 *
 * A line of quantity 1 is granted where it stands. A line of quantity n > 1
 * becomes n − 1 paid units and a new line of 1, inserted right after it, which
 * is the one granted. One unit per line keeps a later refund unambiguous: the
 * free line refunds nothing, the paid line refunds what was paid.
 */
export function splitLineForGrant<L extends CartLineInput>(
  lines: readonly L[],
  lineId: string,
  newLineId: string,
): { lines: L[]; grantedLineId: string } {
  const index = lines.findIndex((line) => line.lineId === lineId);
  const target = lines[index];
  if (!target) throw new GrantError(`No line ${lineId} in the cart`);

  if (target.quantity <= 1) return { lines: [...lines], grantedLineId: lineId };

  const kept: L = { ...target, quantity: target.quantity - 1 };
  const granted: L = { ...target, lineId: newLineId, quantity: 1 };
  const next = [...lines];
  next.splice(index, 1, kept, granted);
  return { lines: next, grantedLineId: newLineId };
}

export interface LineDiscountPart {
  lineId: string;
  grant: Money;
  promo: Money;
}

/**
 * Every line's discount, split into the part a module granted and the part a
 * promotional percentage took. The promo is allocated over what each line
 * still costs after its grant.
 */
export function lineDiscountParts(
  lines: readonly CartLineInput[],
  granted: ReadonlySet<string>,
  promoRateBp: number | null,
): LineDiscountPart[] {
  const grants = lines.map((line) => (granted.has(line.lineId) ? grantAmount(line) : ZERO));

  let promos: Money[] = lines.map(() => ZERO);
  if (promoRateBp !== null && lines.length > 0) {
    const remaining = lines.map((line, index) => {
      const modifierTotal = sum(line.modifiers.map((m) => m.priceAdjustment));
      const subtotal = mul(clampAtZero(add(line.unitPrice, modifierTotal)), line.quantity);
      return { lineId: line.lineId, lineSubtotal: clampAtZero(sub(subtotal, grants[index] ?? ZERO)) };
    });
    const allocated = allocatePromoDiscount(remaining, promoRateBp);
    promos = allocated.map((part) => part.amount);
  }

  return lines.map((line, index) => ({
    lineId: line.lineId,
    grant: grants[index] ?? ZERO,
    promo: promos[index] ?? ZERO,
  }));
}

/** The lines with each one's total discount (grant + promo) set, ready to price. */
export function withLineDiscounts<L extends CartLineInput>(
  lines: readonly L[],
  parts: readonly LineDiscountPart[],
): L[] {
  const byLine = new Map(parts.map((part) => [part.lineId, add(part.grant, part.promo)]));
  return lines.map((line) => ({ ...line, discount: byLine.get(line.lineId) ?? ZERO }));
}
